/**
 * Shrinks an uploaded image so it can actually be stored.
 *
 * WHY THIS EXISTS
 * Images picked here are kept as data URIs inside the home page config, which
 * lives in localStorage. Two facts make that unforgiving:
 *
 *  - base64 inflates a file by about a third, and
 *  - localStorage is roughly 5 MB for the WHOLE origin — shared with every
 *    module, every user record and every scratch layer.
 *
 * So a 5 MB background would arrive as ~6.8 MB of text and could not be stored
 * at all. Worse, `writeJson` swallows the quota error, so it would look saved
 * until the page was reloaded and the picture was gone.
 *
 * Raising the upload limit therefore is not a matter of changing a number: the
 * file has to be re-encoded on the way in. A person picks the photo they have;
 * making it fit is the app's job, not theirs.
 */

/** What the caller gets back, so the UI can say what happened. */
export interface ResizedImage {
  dataUrl: string;
  /** Bytes of the original file. */
  originalBytes: number;
  /** Bytes the data URI will occupy in storage. */
  storedBytes: number;
  width: number;
  height: number;
  /** True when the image was re-encoded rather than passed through. */
  changed: boolean;
}

export interface ResizeOptions {
  /** Longest edge, in pixels, after scaling. */
  maxDimension: number;
  /** Target size for the stored data URI. Quality drops until it is met. */
  targetBytes: number;
}

/** A data URI's real length in bytes, which is what storage will hold. */
function byteLength(dataUrl: string): number {
  return new Blob([dataUrl]).size;
}

/** Loads a file into an image element, cleaning up the object URL either way. */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file could not be read as an image.'));
    };
    image.src = url;
  });
}

/**
 * Whether a format keeps transparency.
 *
 * A logo is the case that matters: re-encoding a transparent PNG as JPEG fills
 * every transparent pixel with black, which is a far worse outcome than a
 * larger file.
 */
function keepsAlpha(type: string): boolean {
  return type === 'image/png' || type === 'image/webp' || type === 'image/gif' || type === 'image/svg+xml';
}

/** Whether this browser can actually produce the format it was asked for. */
function canEncode(type: string): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL(type).startsWith(`data:${type}`);
}

export async function resizeImage(file: File, options: ResizeOptions): Promise<ResizedImage> {
  const originalBytes = file.size;

  /* SVG is already small and resolution-independent; rasterising it would make
     it bigger AND blurrier, so it is passed through untouched. */
  if (file.type === 'image/svg+xml') {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('That file could not be read.'));
      reader.readAsDataURL(file);
    });
    return {
      dataUrl,
      originalBytes,
      storedBytes: byteLength(dataUrl),
      width: 0,
      height: 0,
      changed: false,
    };
  }

  const image = await loadImage(file);
  const longest = Math.max(image.naturalWidth, image.naturalHeight);
  const scale = Math.min(1, options.maxDimension / Math.max(1, longest));

  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser could not process the image.');

  // High-quality downscaling; the default is a cheap nearest-neighbour that
  // makes shrunk logos look ragged.
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(image, 0, 0, width, height);

  /* WebP first: it carries transparency AND compresses far better than PNG, so
     a logo keeps its alpha without paying PNG's size. Falls back to PNG where
     the browser cannot encode WebP, and to JPEG only when alpha is not needed. */
  const wantsAlpha = keepsAlpha(file.type);
  const format = canEncode('image/webp')
    ? 'image/webp'
    : wantsAlpha
      ? 'image/png'
      : 'image/jpeg';

  // PNG ignores the quality argument, so there is nothing to step down.
  const qualities = format === 'image/png' ? [undefined] : [0.9, 0.8, 0.7, 0.6, 0.5, 0.4];

  let dataUrl = canvas.toDataURL(format, qualities[0]);
  for (const quality of qualities) {
    dataUrl = canvas.toDataURL(format, quality);
    if (byteLength(dataUrl) <= options.targetBytes) break;
  }

  /* Still too big at the lowest quality — usually a very large photograph. Halve
     the dimensions and try once more rather than handing back something that
     cannot be stored. */
  if (byteLength(dataUrl) > options.targetBytes && format !== 'image/png') {
    const half = document.createElement('canvas');
    half.width = Math.max(1, Math.round(width / 2));
    half.height = Math.max(1, Math.round(height / 2));
    const halfContext = half.getContext('2d');
    if (halfContext) {
      halfContext.imageSmoothingEnabled = true;
      halfContext.imageSmoothingQuality = 'high';
      halfContext.drawImage(image, 0, 0, half.width, half.height);
      const smaller = half.toDataURL(format, 0.6);
      if (byteLength(smaller) < byteLength(dataUrl)) {
        return {
          dataUrl: smaller,
          originalBytes,
          storedBytes: byteLength(smaller),
          width: half.width,
          height: half.height,
          changed: true,
        };
      }
    }
  }

  return {
    dataUrl,
    originalBytes,
    storedBytes: byteLength(dataUrl),
    width,
    height,
    changed: true,
  };
}

/** Bytes written the way a person reads them. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
